import React, { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/controls/Modal";
import Input from "@/components/ui/input";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import Chip from "@/components/ui/chip";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AddSmall,
  EmptyRecipientsIllustration,
  Info
} from "@dreamhub/designsystem/icons";
import { cn } from "@/utils/utils";
import { ProposalRecipientRole, ProposalRecipients } from "../types";
import {
  addRecipient,
  canOrder,
  candidateFromEmail,
  hasRoomForSigner,
  isAdded,
  isValidEmail,
  moveRecipient,
  RecipientCandidate,
  removeRecipient,
  setOrdered,
  setRecipientRole
} from "./recipients";
import {
  useDealRecipientCandidates,
  useRecipientSearch
} from "./useRecipientPeople";
import { RecipientList } from "./RecipientList";

// How many of the deal's people show before "View all".
const QUICK_ADD_LIMIT = 4;
const NO_RESULT = -1;

interface DialogProps {
  open: boolean;
  recipients: ProposalRecipients;
  signaturePlaces: number;
  // The deal's people, offered as quick adds.
  dealPeople: RecipientCandidate[];
  // People matching what the rep last searched for.
  searchResults: RecipientCandidate[];
  onSearch: (term: string) => void;
  onContinue: (recipients: ProposalRecipients) => void;
  onCancel: () => void;
  // In place of "Continue", where the step saves rather than moves on.
  confirmLabel?: string;
}

type FormProps = Omit<DialogProps, "open">;

const RecipientsForm: React.FC<FormProps> = ({
  recipients: initial,
  signaturePlaces,
  dealPeople: candidates,
  searchResults,
  onSearch,
  onContinue,
  onCancel,
  confirmLabel
}) => {
  const { t } = useTranslation();
  const listboxId = useId();
  const [draft, setDraft] = useState(initial);
  const [term, setTermState] = useState("");
  const [showAllDealPeople, setShowAllDealPeople] = useState(false);
  // A result is picked by Enter only once the arrows reach it, so Enter on a
  // typed address adds the address even when people match it.
  const [activeResult, setActiveResult] = useState(NO_RESULT);
  const [resultsOpen, setResultsOpen] = useState(true);

  const setTerm = (next: string) => {
    setTermState(next);
    onSearch(next);
  };

  const typedEmail = term.trim();
  // Only a term shaped like an address is checked as one; anything else is a
  // search for a person.
  const looksLikeEmail = typedEmail.includes("@");
  const emailError = looksLikeEmail && !isValidEmail(typedEmail);
  const emailCandidate =
    looksLikeEmail && !emailError ? candidateFromEmail(typedEmail) : undefined;
  const emailAlreadyAdded = !!emailCandidate && isAdded(draft, emailCandidate);
  const canAddEmail = !!emailCandidate && !emailAlreadyAdded;
  const fieldMessage = emailError || emailAlreadyAdded;

  const results = useMemo(
    () => searchResults.filter((candidate) => !isAdded(draft, candidate)),
    [searchResults, draft]
  );
  const showResults = resultsOpen && results.length > 0;

  const dealPeople = useMemo(
    () => candidates.filter((candidate) => !isAdded(draft, candidate)),
    [candidates, draft]
  );
  const visibleDealPeople = showAllDealPeople
    ? dealPeople
    : dealPeople.slice(0, QUICK_ADD_LIMIT);

  const signerUnavailableReason = hasRoomForSigner(draft.list, signaturePlaces)
    ? undefined
    : t("contracts.proposals.recipients.noSignaturePlace", {
        count: signaturePlaces
      });

  const add = (candidate: RecipientCandidate) =>
    setDraft((current) => addRecipient(current, candidate, signaturePlaces));

  const addFromField = (candidate: RecipientCandidate) => {
    add(candidate);
    setTerm("");
    setActiveResult(NO_RESULT);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === "ArrowDown" && showResults) {
      event.preventDefault();
      setActiveResult((index) => Math.min(index + 1, results.length - 1));
    } else if (event.key === "ArrowUp" && showResults) {
      event.preventDefault();
      setActiveResult((index) => Math.max(index - 1, NO_RESULT));
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (showResults && results[activeResult]) {
        addFromField(results[activeResult]);
      } else if (canAddEmail && emailCandidate) {
        addFromField(emailCandidate);
      }
    }
  };

  const onRoleChange = (key: string, role: ProposalRecipientRole) =>
    setDraft((current) =>
      setRecipientRole(current, key, role, signaturePlaces)
    );

  return (
    <>
      {/* Only the recipient list scrolls; adding people stays in view above it. */}
      <div className="flex flex-col gap-6 h-[512px] px-6 pt-6">
        {/* Room for the message under the field, which sits outside its row. */}
        <div className={cn("flex items-end gap-4", fieldMessage && "mb-4")}>
          <div className="relative flex flex-col flex-1 gap-1 min-w-0">
            <Text
              as="label"
              variant="bodySecondary"
              className="pl-1"
              htmlFor={`${listboxId}-field`}
            >
              {t("contracts.proposals.recipients.addLabel")}
            </Text>
            <Input
              id={`${listboxId}-field`}
              value={term}
              onChange={(event) => {
                setTerm(event.target.value);
                setActiveResult(NO_RESULT);
                setResultsOpen(true);
              }}
              onKeyDown={onKeyDown}
              onFocus={() => setResultsOpen(true)}
              onBlur={() => setResultsOpen(false)}
              placeholder={t("contracts.proposals.recipients.addPlaceholder")}
              hasError={emailError}
              autoComplete="off"
              role="combobox"
              aria-expanded={showResults}
              aria-controls={listboxId}
              aria-invalid={emailError || undefined}
              aria-describedby={fieldMessage ? `${listboxId}-error` : undefined}
              aria-activedescendant={
                showResults && activeResult !== NO_RESULT
                  ? `${listboxId}-${activeResult}`
                  : undefined
              }
              data-test="recipients-field"
            />
            {emailError ? (
              <Text
                variant="smBodyError"
                id={`${listboxId}-error`}
                className="absolute top-full mt-1 pl-1"
                data-test="recipients-email-error"
              >
                {t("contracts.proposals.recipients.invalidEmail")}
              </Text>
            ) : null}
            {emailAlreadyAdded ? (
              <div
                id={`${listboxId}-error`}
                className="absolute top-full mt-1 pl-1 flex items-center gap-1"
                data-test="recipients-email-duplicate"
              >
                <IconContainer as="span" size="sm" color="secondary">
                  <Info />
                </IconContainer>
                <Text variant="smBodySecondary">
                  {t("contracts.proposals.recipients.alreadyAdded")}
                </Text>
              </div>
            ) : null}
            {showResults ? (
              <ul
                id={listboxId}
                role="listbox"
                className="absolute top-full left-0 right-0 z-10 mt-2 flex flex-col gap-1 max-h-[320px] overflow-y-auto p-1 rounded-[12px] border border-border-field-droplist bg-surface-droplist-popover shadow-tooltip"
                data-test="recipients-results"
              >
                {results.map((candidate, index) => (
                  <li
                    key={candidate.key}
                    id={`${listboxId}-${index}`}
                    role="option"
                    aria-selected={index === activeResult}
                    // Keeps focus in the field, so typing carries on.
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => addFromField(candidate)}
                    className={cn(
                      "flex flex-col pl-2 pr-3 py-2 rounded-lg cursor-pointer hover:bg-surface-hover-soft",
                      index === activeResult && "bg-surface-hover-soft"
                    )}
                    data-test={`recipients-result-${candidate.key}`}
                  >
                    <Text
                      variant="body"
                      className="truncate"
                      hasTruncatedTooltip
                    >
                      {candidate.name}
                    </Text>
                    <Text
                      variant="smBodySecondary"
                      className="truncate"
                      hasTruncatedTooltip
                    >
                      {candidate.jobTitle ?? candidate.email}
                    </Text>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <Button
            variant="primary"
            size="lg"
            disabled={!canAddEmail}
            onClick={() => emailCandidate && addFromField(emailCandidate)}
            data-test="recipients-add"
          >
            {t("contracts.proposals.recipients.add")}
          </Button>
        </div>

        {dealPeople.length > 0 ? (
          <div
            className="flex flex-col gap-2"
            data-test="recipients-deal-people"
          >
            <Text variant="bodySecondary" className="pl-1">
              {t("contracts.proposals.recipients.dealPeople")}
            </Text>
            <div
              className={cn(
                "flex flex-wrap items-center gap-2.5",
                // Three rows of chips, then the rest scroll.
                showAllDealPeople && "max-h-[106px] overflow-y-auto p-px"
              )}
              data-test="recipients-deal-people-chips"
            >
              {visibleDealPeople.map((candidate) => (
                <Chip
                  key={candidate.key}
                  size="md"
                  filled
                  role="button"
                  tabIndex={0}
                  onClick={() => add(candidate)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      add(candidate);
                    }
                  }}
                  aria-label={t("contracts.proposals.recipients.addPerson", {
                    name: candidate.name
                  })}
                  className="gap-1 px-1.5 shadow-field focus-visible:ring-1 focus-visible:ring-border-field-focus"
                  icon={
                    <IconContainer size="sm" color="primary">
                      <AddSmall />
                    </IconContainer>
                  }
                  label={candidate.name}
                  data-test={`recipients-deal-person-${candidate.key}`}
                />
              ))}
              {!showAllDealPeople && dealPeople.length > QUICK_ADD_LIMIT ? (
                <Button
                  variant="tertiary"
                  onClick={() => setShowAllDealPeople(true)}
                  data-test="recipients-view-all"
                >
                  {t("contracts.proposals.recipients.viewAll")}
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="h-px shrink-0 bg-border-border1" />

        <div className="flex flex-col flex-1 gap-2 min-h-0">
          <div className="flex items-center justify-between gap-2 min-h-8">
            <Text variant="bodySecondary" className="pl-1">
              {t("contracts.proposals.recipients.listLabel")}
            </Text>
            {canOrder(draft.list) ? (
              <label className="flex items-center gap-2 cursor-pointer">
                <Switch
                  checked={draft.ordered}
                  onCheckedChange={(ordered) =>
                    setDraft((current) => setOrdered(current, ordered))
                  }
                  data-test="recipients-order"
                />
                <Text as="span" variant="body">
                  {t("contracts.proposals.recipients.setOrder")}
                </Text>
              </label>
            ) : null}
          </div>
          {draft.list.length > 0 ? (
            <div
              className="flex-1 min-h-0 overflow-y-auto"
              data-test="recipients-list-scroll"
            >
              <RecipientList
                recipients={draft.list}
                ordered={draft.ordered}
                signerUnavailableReason={signerUnavailableReason}
                onRoleChange={onRoleChange}
                onRemove={(key) =>
                  setDraft((current) => removeRecipient(current, key))
                }
                onMove={(from, to) =>
                  setDraft((current) => moveRecipient(current, from, to))
                }
              />
            </div>
          ) : (
            <div
              className="flex flex-col items-center gap-2 px-2 py-6"
              data-test="recipients-empty"
            >
              {/* The artwork's shadow spills past its 46×50 frame. */}
              <div className="flex items-center justify-center size-[50px] shrink-0">
                <div className="relative w-[46px] h-[50px]">
                  <div
                    aria-hidden
                    className="absolute left-[-16.25px] top-[-8.25px]"
                  >
                    <EmptyRecipientsIllustration />
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-center text-center">
                <Text variant="body">
                  {t("contracts.proposals.recipients.emptyTitle")}
                </Text>
                <Text variant="bodyTertiary">
                  {t("contracts.proposals.recipients.emptyDescription")}
                </Text>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center justify-end gap-2 pl-5 pr-4 py-4 border-t border-border-border1">
        <Button
          variant="secondary"
          onClick={onCancel}
          data-test="recipients-cancel"
        >
          {t("cancel")}
        </Button>
        <Button
          variant="primary"
          onClick={() => onContinue(draft)}
          data-test="recipients-continue"
        >
          {confirmLabel ?? t("contracts.proposals.recipients.continue")}
        </Button>
      </div>
    </>
  );
};

export const RecipientsDialog: React.FC<DialogProps> = ({
  open,
  onCancel,
  ...formProps
}) => {
  const { t } = useTranslation();
  // The dialog unmounts its content when it closes, so each opening starts a
  // fresh form from the recipients given.
  return (
    <Modal
      open={open}
      setIsOpen={(next) => {
        if (!next) onCancel();
      }}
      title={<>{t("contracts.proposals.recipients.title")}</>}
      contentClassname="w-[720px]"
      hideDivider
      data-test="recipients-modal"
      content={<RecipientsForm onCancel={onCancel} {...formProps} />}
    />
  );
};

interface ModalProps
  extends Omit<DialogProps, "dealPeople" | "searchResults" | "onSearch"> {
  dealId: string;
}

// Offers the deal's people and searches the account's, from the live data.
export const RecipientsModal: React.FC<ModalProps> = ({
  dealId,
  open,
  ...dialogProps
}) => {
  const [term, setTerm] = useState("");
  useEffect(() => {
    if (open) setTerm("");
  }, [open]);
  const { candidates } = useDealRecipientCandidates(dealId, open);
  const { results } = useRecipientSearch(open ? term : "");
  return (
    <RecipientsDialog
      open={open}
      dealPeople={candidates}
      searchResults={results}
      onSearch={setTerm}
      {...dialogProps}
    />
  );
};
